import vtkActor from 'vtk.js/Sources/Rendering/Core/Actor';
import vtkAppendPolyData from 'vtk.js/Sources/Filters/General/AppendPolyData';
import vtkMath from 'vtk.js/Sources/Common/Core/Math';
import vtkTexture from 'vtk.js/Sources/Rendering/Core/Texture';
import { DesiredOutputPrecision } from 'vtk.js/Sources/Common/DataModel/DataSetAttributes/Constants';

import { mat4, vec3 } from 'gl-matrix';

import { IMeshNode, MarginLine } from '@csdental/dicom-reader';
import { createMeshActor, createPolyDataActor, createPolyData, setActorBackface } from '../shared/vtkUtils';

import { MeshName, SelectableMesh } from '../shared/common';

const isBuccalBite = (name: string) => {
  return name.includes(MeshName.BiteAverage);
};

const isUpperJaw = (name: string) => {
  return name.includes(MeshName.Maxillary);
};

const isLowerJaw = (name: string) => {
  return name.includes(MeshName.Mandibular);
};

abstract class Mesh {
  name: string;
  mesh: IMeshNode;

  transparency: number;

  visible: boolean;
  checked: boolean;

  color: boolean;
  light: boolean;

  actor: vtkActor;
  texture: vtkTexture;

  rotation: vec3;
  movement: vec3;

  matrix: mat4;

  preferColor = { r: 200 / 255.0, g: 200 / 255.0, b: 200 / 255.0 };

  constructor(name: string) {
    this.name = name;
    this.rotation = [0, 0, 0];
    this.movement = [0, 0, 0];
  }

  abstract build(): void;

  isNormalMesh = () => {
    return !isBuccalBite(this.name);
  };

  isSelectable = () => {
    return SelectableMesh.includes(this.name as MeshName);
  };

  isCommonScanMesh = () => {
    return this.name == 'Maxillary' || this.name == 'Mandibular';
  };

  isEdentulousScanMesh = () => {
    return this.name.includes('Edentulous');
  };

  isBuccalMesh = () => {
    return isBuccalBite(this.name);
  };

  isUpperJaw = () => {
    return isUpperJaw(this.name);
  };

  isLowerJaw = () => {
    return isLowerJaw(this.name);
  };

  isCommonUpperJaw = () => {
    return MeshName.Maxillary === this.name || MeshName.MaxillaryAnatomy === this.name;
  };

  isCommonLowerJaw = () => {
    return MeshName.Mandibular === this.name || MeshName.MandibularAnatomy === this.name;
  };

  isTrueColorSupported() {
    return this.isTextured() || this.hasScalar();
  }

  hasScalar() {
    if (!this.actor) {
      return false;
    }
    const polydata = this.actor.getMapper().getInputData();
    if (!polydata) {
      return false;
    }
    const scalars = polydata.getPointData().getScalars();
    if (!scalars || !scalars.getData() || scalars.getNumberOfValues() <= 0) {
      return false;
    }
    return true;
  }

  setVisible = (visible: boolean) => {
    this.visible = visible;
    if (this.actor) {
      this.actor.setVisibility(visible);
    }
  };

  setTransparency = (arg: number) => {
    if (null == this.actor) {
      return;
    }
    let op = 1 - arg / 100.0;
    this.actor.getProperty().setOpacity(op);
    this.setBackface(this.isBackfaceAvailable());
  };

  switchLight = (light: boolean) => {
    if (this.actor) {
      this.actor.getProperty().setAmbient(light ? 1 : 0);
      this.actor.getProperty().setDiffuse(light ? 0 : 1);
    }
  };

  switchColor = (color: boolean) => {
    if (this.texture != null) {
      color ? this.revertTexture() : this.removeTexture();
    }
  };

  setBackface(enabled: boolean) {
    setActorBackface(this.actor, enabled);
  }

  revertTexture = () => {
    if (this.actor) {
      this.actor.addTexture(this.texture);
      this.setBackface(this.isBackfaceAvailable());
    }
  };

  removeTexture = () => {
    if (this.actor) {
      this.actor.removeTexture(this.texture);
      this.setBackface(this.isBackfaceAvailable());
    }
  };

  isTextured = () => {
    return this.texture != null;
  };

  isTransparented() {
    if (this.actor) {
      const op = this.actor.getProperty().getOpacity();
      if (op < 1) {
        return true;
      }
    }
    return false;
  }

  isBackfaceAvailable() {
    if (this.actor && this.texture && this.actor.hasTexture(this.texture)) {
      return false;
    }
    if (this.isTransparented()) {
      return false;
    }
    return true;
  }

  // for orientation adjustment
  rotateX(arg) {
    if (this.actor == null) {
      return;
    }
    this.rotation[0] = arg;
    this.doTransform();
  }

  rotateY(arg) {
    if (this.actor == null) {
      return;
    }
    this.rotation[1] = arg;
    this.doTransform();
  }

  rotateZ(arg) {
    if (this.actor == null) {
      return;
    }
    this.rotation[2] = arg;
    this.doTransform();
  }

  doMovement(arg) {
    if (this.actor == null) {
      return;
    }
    this.movement[2] = arg;
    this.doTransform();
  }

  doTransform() {
    const m = mat4.create();
    mat4.rotateX(m, m, vtkMath.radiansFromDegrees(this.rotation[0]));
    mat4.rotateY(m, m, vtkMath.radiansFromDegrees(this.rotation[1]));
    mat4.rotateZ(m, m, vtkMath.radiansFromDegrees(this.rotation[2]));
    mat4.translate(m, m, this.movement);
    this.actor.setUserMatrix(m);
    this.matrix = m;
  }

  doTransformWithMatrix(matrix: mat4) {
    let m = matrix;
    mat4.transpose(m, matrix);
    this.actor.setUserMatrix(m);
  }

  hasAdjustments() {
    const x = this.rotation[0];
    const y = this.rotation[1];
    const z = this.rotation[2];
    const m = this.movement[2];
    return x !== 0 || y !== 0 || z !== 0 || m !== 0;
  }

  applyAdjustments() {
    this.actor?.setUserMatrix(this.matrix);
  }
}

class NormalMesh extends Mesh {
  marginLine: MarginLine[];

  constructor(name: string) {
    super(name);
    this.visible = true;
  }

  async build() {
    const [actor, texture] = await createMeshActor(this.mesh);

    this.actor = actor;
    this.texture = texture;
    this.setBackface(this.isBackfaceAvailable());
  }
}

class BuccalMesh extends Mesh {
  constructor(name: string) {
    super(name);
    this.visible = false;
  }

  build() {
    /* build bite matrix */
    this.matrix = mat4.create();
    for (let i = 0; i < this.mesh.biteMatrix.length; i++) {
      this.matrix[i] = Number(this.mesh.biteMatrix[i]);
    }

    /* build bite mesh, merge 6 (at most) meshes into one unique mesh */
    if (this.mesh.children == null || this.mesh.children.length == 0) {
      return;
    }

    const appender = vtkAppendPolyData.newInstance();
    for (let i = 0; i < this.mesh.children.length; ++i) {
      const childItem = this.mesh.children[i];
      const port = createPolyData(childItem.meshBuffer, childItem.meshBufferType);
      if (i > 0) {
        appender.addInputConnection(port);
      } else {
        // vtk addInputData bug
        appender.setInputConnection(port);
      }
    }
    appender.setOutputPointsPrecision(DesiredOutputPrecision.DEFAULT);
    appender.update();

    this.actor = createPolyDataActor(appender.getOutputData());
  }
}

const buildMesh = async (mesh: IMeshNode) => {
  const isBuccal = isBuccalBite(mesh.meshPart);

  let finalMesh = null as Mesh;
  if (isBuccal) {
    const biteMesh = new BuccalMesh(mesh.biteName);
    finalMesh = biteMesh;
  } else {
    const normMesh = new NormalMesh(mesh.meshPart);
    finalMesh = normMesh;
  }

  finalMesh.mesh = mesh;
  finalMesh.build();

  return finalMesh;
};

class OccluMesh extends Mesh {
  constructor(name: string) {
    super(name);
    this.visible = true;
  }
  build() {
    console.log('OccluMesh build');
  }
}

export { Mesh, buildMesh, isBuccalBite, OccluMesh, isLowerJaw, isUpperJaw };
